import React from "react";
import { formatDisplayDate } from "../../../../common/utils/date";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
// Edit is disabled for now — see the commented-out edit action below.
// import EditSvg from "../../../../common/svgs/EditSvg";
import BinSvg from "../../../../common/svgs/BinSvg";
import ScrollableWrapper from "../common/ScrollableWrapper";
import PanelSummaryCard from "../common/PanelSummaryCard";
import {
  resolvePanelLabStatus,
  getStatusBadgeStyle,
} from "../../utils/labPanelStatus";

interface PanelDetailsViewProps {
  panel: any;
  // Editing a custom panel is switched off for now: the header action deletes
  // instead. The handler is still wired up in PanelDetailsViewWrapper so this
  // can be turned back on by restoring the commented-out button below.
  onEdit?: () => void;
  // Confirm-then-delete this panel (the wrapper owns the snackbar + call).
  onDelete: () => void;
  // Provider catalog panels are read-only — no header action at all.
  hideActions?: boolean;
}

const PanelDetailsView: React.FC<PanelDetailsViewProps> = ({
  panel,
  onDelete,
  hideActions = false,
}) => {
  const theme: Theme = useTheme();
  // Null for provider catalog panels, which carry no lab status.
  const labStatus = resolvePanelLabStatus(panel);

  return (
    <div style={styles.container}>
      {/* No title or back button: the breadcrumb names the page and carries
          the link back to the listing. The card below states the panel's
          own name. */}
      <div style={styles.contentWrapper}>
        <ScrollableWrapper>
          <div style={styles.contentContainer(theme)}>
            {/* Panel name + edit */}
            <div style={styles.panelHeader}>
              <div style={styles.panelHeaderLeft}>
                <div style={styles.panelName(theme)}>{panel.name}</div>
                <div style={styles.panelDate(theme)}>
                  {formatDisplayDate(panel.dateUpdated || panel.dateCreated)}
                </div>
              </div>
              <div style={styles.panelHeaderRight}>
                {labStatus && (
                  <div style={getStatusBadgeStyle(labStatus.tone, theme)}>
                    {labStatus.label}
                  </div>
                )}
                {!hideActions && (
                  <div style={styles.actionButton} onClick={onDelete}>
                    <BinSvg color={theme.colors_ds.PRIMARY_ICON_MAIN} />
                  </div>
                )}
                {/* Edit action — replaced by the delete above for now:
                {!hideActions && (
                  <div style={styles.actionButton} onClick={onEdit}>
                    <EditSvg color={theme.colors_ds.PRIMARY_ICON_MAIN} />
                  </div>
                )} */}
              </div>
            </div>

            <PanelSummaryCard panel={panel} />
          </div>
        </ScrollableWrapper>
      </div>
    </div>
  );
};

export default PanelDetailsView;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    height: "100%",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden" as "hidden",
  },
  contentContainer: (theme: Theme) => ({
    padding: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  }),
  panelHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  panelHeaderLeft: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  // Status badge + the header action icon, in that order.
  panelHeaderRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  panelName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  panelDate: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  actionButton: {
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
  },
};
